export interface AuditPayloadRow {
  key: string;
  label: string;
  value: string;
}

export interface AuditPayloadDeps {
  /** Spanish name for a payload key (`audit_labels.field.*`). */
  fieldLabel: (key: string) => string;
  /** Spanish name for an enum-like value (`audit_labels.value.*`). */
  valueLabel: (raw: string) => string;
  /** Display name of a user id, or null when it cannot be resolved. */
  userName: (id: string) => string | null;
  formatMoney: (cents: string) => string;
  formatDate: (iso: string) => string;
  yes: string;
  no: string;
  empty: string;
}

/** Keys whose value is a user id: shown as the person's name, hidden when unresolvable. */
const USER_KEYS = new Set([
  'actor_user_id',
  'cashier_id',
  'veterinarian_id',
  'recorded_by',
  'removed_by',
  'closed_by',
  'cancelled_by',
  'voided_by',
  'taken_by',
]);

/** Keys whose string value is an internal enum code and gets a Spanish label. */
const ENUM_KEYS = new Set([
  'status',
  'movement_status',
  'movement_kind',
  'kind',
  'study_kind',
  'diagnosis_kind',
  'visit_kind',
  'note_kind',
  'source',
  'weight_source',
  'scope',
  'triggered_by',
  'operation',
  'section',
  'via',
  'language',
  'metodo_pago',
  'forma_pago',
]);

/** Technical plumbing with no meaning to an auditor. */
const HIDDEN_KEYS = new Set([
  'content_hash',
  'skald_request_id',
  'compression',
  'where',
  'route',
  'external_ref',
  'manager',
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function isOpaqueIdKey(key: string): boolean {
  return /(^|_)ids?$/.test(key) && key !== 'id';
}

function formatScalar(key: string, value: unknown, deps: AuditPayloadDeps): string | null {
  if (value === null || value === undefined || value === '') return deps.empty;
  if (typeof value === 'boolean') return value ? deps.yes : deps.no;
  if (key.endsWith('_cents') && (typeof value === 'string' || typeof value === 'number')) {
    return /^-?\d+$/.test(String(value)) ? deps.formatMoney(String(value)) : String(value);
  }
  if (key === 'weight_grams' && typeof value === 'number') return `${value} g`;
  if (typeof value === 'string') {
    if (UUID_RE.test(value)) return null; // opaque id: never useful to read
    if (ISO_RE.test(value)) return deps.formatDate(value);
    if (ENUM_KEYS.has(key)) return deps.valueLabel(value);
    return value;
  }
  if (typeof value === 'number') return String(value);
  return null;
}

function formatValue(key: string, value: unknown, deps: AuditPayloadDeps): string | null {
  if (USER_KEYS.has(key)) {
    return typeof value === 'string' ? deps.userName(value) : null;
  }
  if (isOpaqueIdKey(key)) return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return deps.empty;
    // Arrays of field names (`fields_changed`, `sections`) read as Spanish field labels.
    if (value.every((v) => typeof v === 'string')) {
      const names = (value).map((v) =>
        key === 'fields_changed' || key === 'sections' || key === 'applied_sections'
          ? deps.fieldLabel(v)
          : v,
      );
      return names.join(', ');
    }
    return `${value.length}`;
  }
  if (value !== null && typeof value === 'object') {
    const inner = flatten(value as Record<string, unknown>, deps);
    return inner.length === 0 ? null : inner.map((r) => `${r.label}: ${r.value}`).join(' · ');
  }
  return formatScalar(key, value, deps);
}

function flatten(obj: Record<string, unknown>, deps: AuditPayloadDeps): AuditPayloadRow[] {
  const rows: AuditPayloadRow[] = [];
  for (const [key, value] of Object.entries(obj)) {
    if (HIDDEN_KEYS.has(key)) continue;
    const formatted = formatValue(key, value, deps);
    if (formatted === null) continue;
    rows.push({ key, label: deps.fieldLabel(key), value: formatted });
  }
  return rows;
}

/**
 * Turns the JSON string of a `previous_state` / `new_state` / `metadata` column into readable
 * rows: Spanish field names, money as currency, enums translated, booleans as Sí/No, user ids as
 * names. Opaque ids and technical plumbing are dropped. Unparseable input yields no rows.
 */
export function buildAuditPayloadRows(raw: string | null, deps: AuditPayloadDeps): AuditPayloadRow[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  return flatten(parsed as Record<string, unknown>, deps);
}
